/**
 * @Author: Alex Sorafumo <Yuion>
 * @Date:   28/10/2016 11:24 AM
 * @Email:  alex@yuion.net
 * @Filename: virtual-keyboard.component.ts
 * @Last modified by:   Yuion
 * @Last modified time: 15/12/2016 11:32 AM
 */

import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';

const KEY_LIST = {
    QWERTY : {
        normal: [
            '`1234567890-=',
            'qwertyuiop[]\\',
            'asdfghjkl;\'',
            'zxcvbnm,./',
        ],
        shift: [
            '~!@#$%^&*()_+',
            'QWERTYUIOP{}',
            'ASDFGHJKL:"',
            'ZXCVBNM<>?',
        ],
    },
    NUMPAD : {
        normal: [
            '123', '456', '789', '*0#',
        ],
        shift: [
            '123', '456', '789', '*0#',
        ],
    },
};

@Component({
  selector: 'keyboard-display',
  styles: [`.virtual-keyboard{position:fixed;z-index:9999}.virtual-keyboard.standard{left:50%;-webkit-transform:translateX(-50%);transform:translateX(-50%)}.topbar{cursor:-webkit-grab;cursor:grab;padding:.25em .5em .5em;text-align:right}.topbar:active{cursor:-webkit-grabbing;cursor:grabbing}.close{cursor:pointer;display:inline-block}.close:hover{color:rgba(0,0,0,.54)}.keyboard{background-color:#fff;border-radius:.2em;-webkit-box-shadow:0 .1em .2em .1em rgba(0,0,0,.2);box-shadow:0 .1em .2em .1em rgba(0,0,0,.2);font-size:1.2em;margin:.5em;padding:.25em;text-align:center;z-index:9999}@media screen and (max-width:720px){.keyboard{font-size:1.5em}}.keyboard.simple{-webkit-box-shadow:none;box-shadow:none}.keyboard.left{left:0;position:fixed;top:30%}.keyboard.right{position:fixed;right:0;top:30%}.keyboard .row{-webkit-box-align:center;-ms-flex-align:center;align-items:center;display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center}.key{background-color:#fff;border-radius:.2em;-webkit-box-shadow:0 .1em .2em .1em rgba(0,0,0,.2);box-shadow:0 .1em .2em .1em rgba(0,0,0,.2);cursor:pointer;font-size:1em;height:2.2em;margin:.25em;padding:.5em 1em}.key.normal{padding:.5em .75em;width:2.5em}.key:hover{background-color:rgba(0,0,0,.2)}.key:active{-webkit-box-shadow:0 .1em .2em .1em rgba(0,0,0,.2);box-shadow:0 .1em .2em .1em rgba(0,0,0,.2)}.key.active{background-color:#4caf50;color:#fff}`],
  template: `<!--
@Author: Alex Sorafumo <Yuion>
@Date:   28/10/2016 11:24 AM
@Email:  alex@yuion.net
@Filename: virtual-keyboard.template.html
@Last modified by:   Yuion
@Last modified time: 15/12/2016 11:32 AM
-->

<div #main [class]="'virtual-keyboard ' + layout" [style.top]="top" [style.left]="left">
    <div class="keyboard standard" [@keyboard]="active?'show':'hide'" *ngIf="layout==='standard'">
        <div class="topbar" (panmove)="moveKeyboard($event);"><div class="close" (touchrelease)="close()">&#x2573;</div></div>
        <div class="keyboard simple">
            <div class="row">
                <div class="key normal" *ngFor="let s of keys[!is_shift?'normal':'shift'][0]" (touchrelease)="addChar(s)">{{s}}</div>
                <div class="key" *ngIf="type!=='NUMPAD'" (touchrelease)="backspace()">Bksp</div>
            </div>
            <div class="row">
                <div class="key normal" *ngFor="let s of keys[!is_shift?'normal':'shift'][1]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key normal" *ngFor="let s of keys[!is_shift?'normal':'shift'][2]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key" *ngIf="type!=='NUMPAD'" [class.active]="is_caps" (touchrelease)="caps()">Caps</div>
                <div class="key normal" *ngFor="let s of keys[!is_shift?'normal':'shift'][3]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key" *ngIf="type!=='NUMPAD'" [class.active]="is_shift" (touchrelease)="shift()">Shift</div>
                <div class="key" *ngIf="type!=='NUMPAD'">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Space&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div class="key" *ngIf="type==='NUMPAD'" (touchrelease)="backspace()">Bksp</div>
                <div class="key" *ngIf="type==='NUMPAD'" (touchrelease)="clear()">Clear</div>
            </div>
        </div>
    </div>
    <div class="split" *ngIf="layout==='split'">
        <div class="keyboard left" [@keyboardleft]="active?'show':'hide'" *ngIf="keys&&keys['left']">
            <div class="row">
                <div class="key normal" *ngFor="let s of keys['left'][!is_shift?'normal':'shift'][0]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key normal" *ngFor="let s of keys['left'][!is_shift?'normal':'shift'][1]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key normal" *ngFor="let s of keys['left'][!is_shift?'normal':'shift'][2]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key" [class.active]="is_caps" (touchrelease)="caps()">Caps</div>
                <div class="key normal" *ngFor="let s of keys['left'][!is_shift?'normal':'shift'][3]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key" [class.active]="is_shift" (touchrelease)="shift()">Shift</div>
                <div class="key">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Space&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
            </div>
        </div>
        <div class="keyboard right" [@keyboardright]="active?'show':'hide'" *ngIf="keys&&keys['right']">
            <div class="row">
                <div class="key normal" *ngFor="let s of keys['right'][!is_shift?'normal':'shift'][0]" (touchrelease)="addChar(s)">{{s}}</div>
                <div class="key" (touchrelease)="backspace()">Bksp</div>
            </div>
            <div class="row">
                <div class="key normal" *ngFor="let s of keys['right'][!is_shift?'normal':'shift'][1]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key normal" *ngFor="let s of keys['right'][!is_shift?'normal':'shift'][2]" (touchrelease)="addChar(s)">{{s}}</div>
            </div>
            <div class="row">
                <div class="key normal" *ngFor="let s of keys['right'][!is_shift?'normal':'shift'][3]" (touchrelease)="addChar(s)">{{s}}</div>
                <div class="key" [class.active]="is_caps" (touchrelease)="caps()">Caps</div>
            </div>
            <div class="row">
                <div class="key">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Space&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div class="key" [class.active]="is_shift" (touchrelease)="shift()">Shift</div>
            </div>
        </div>
    </div>
</div>
`,
  animations: [
      trigger('keyboard', [
          state('hide', style({ opacity : '0', transform: 'scale(0.5)', display: 'none' })),
          state('show', style({ opacity : '1', transform: 'scale(1.0)' })),
          transition('show <=> hide', animate('0.25s ease-out')),
          transition('void => show', animate('0.25s ease-in')),
      ]),
      trigger('keyboardleft', [
          state('hide', style({ opacity : '0', left: '-100%' })),
          state('show', style({ opacity : '1', left: '0' })),
          transition('show <=> hide', animate('0.25s ease-out')),
          transition('void => show', animate('0.25s ease-in')),
      ]),
      trigger('keyboardright', [
          state('hide', style({ opacity : '0', right: '-100%' })),
          state('show', style({ opacity : '1', right: '0' })),
          transition('show <=> hide', animate('0.25s ease-out')),
          transition('void => show', animate('0.25s ease-in')),
      ]),
  ],
})
export class VirtualKeyboardComponent implements OnChanges {
    @Input() public model = '';
    @Input() public active = false;
    @Input() public type = 'QWERTY';
    @Input() public layout = 'standard';
    @Output() public modelChange = new EventEmitter();
    @Output() public activeChange = new EventEmitter();

    public top = '';
    public left = '';
    public top_value = 0;
    public left_value = 0;
    public keys: any = { normal: [], shift: [] };
    public is_shift = false;
    public is_caps = false;

    @ViewChild('main') private keyboard: ElementRef;

    constructor() {
        this.loadKeys();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.type) {
            this.loadKeys();
        }
        if (changes.layout) {
            this.loadLayout();
        }
    }

    public open() {
        setTimeout(() => {
            this.active = true;
        }, 20);
    }

    public close() {
        setTimeout(() => {
            this.active = false; this.activeChange.emit(false);
        }, 20);
    }

    public addChar(char: string) {
        setTimeout(() => {
            this.model += char;
            this.is_shift = false;
            this.modelChange.emit(this.model);
        }, 20);
    }

    public backspace() {
        setTimeout(() => {
            this.model = this.model.substring(0, this.model.length - 1);
            this.modelChange.emit(this.model);
        }, 20);
    }

    public delete() {
        return;
    }

    public clear() {
        setTimeout(() => {
            this.model = '';
            this.modelChange.emit(this.model);
        }, 20);
    }

    public caps() {
        this.is_caps = !this.is_caps;
    }

    public shift() {
        this.is_shift = !this.is_shift;
    }

    public moveKeyboard(e: any) {
        if (e) {
            if (this.keyboard && this.keyboard.nativeElement) {
                const key_box = this.keyboard.nativeElement.getBoundingClientRect();
                if (this.top === '' || this.left === '') {
                    this.top_value = key_box.top;
                    this.left_value = key_box.left;
                } else {
                    this.top_value = e.center.y - 25;
                    this.left_value = e.center.x;
                }
                if (this.top_value < 0) {
                    this.top_value = 0;
                }
                if (this.left_value < key_box.width / 2) {
                    this.left_value = key_box.width / 2;
                }
                this.top = this.top_value + 'px';
                this.left = this.left_value + 'px';
            }
        }
    }

    private loadLayout() {
        setTimeout(() => {
            this.loadKeys();
            if (this.layout === 'split') {
                const keys: any = { left: {}, right: {} };
                const left: any = { normal: [], shift: [] };
                const right: any = { normal: [], shift: [] };

                let n_len = Math.ceil(this.keys.normal[0].length / 2);
                let s_len = Math.ceil(this.keys.shift[0].length / 2);

                left.normal.push(this.keys.normal[0].slice(0, n_len));
                left.shift.push(this.keys.shift[0].slice(0, s_len));
                right.normal.push(this.keys.normal[0].slice(n_len, this.keys.normal[0].length));
                right.shift.push(this.keys.shift[0].slice(s_len, this.keys.shift[0].length));
                for (let i = 1; i < this.keys.normal.length; i++) {
                    n_len = Math.floor(this.keys.normal[i].length / 2);
                    s_len = Math.floor(this.keys.shift[i].length / 2);

                    left.normal.push(this.keys.normal[i].slice(0, n_len));
                    left.shift.push(this.keys.shift[i].slice(0, s_len));
                    right.normal.push(this.keys.normal[i].slice(n_len, this.keys.normal[i].length));
                    right.shift.push(this.keys.shift[i].slice(s_len, this.keys.shift[i].length));
                }
                keys.left = left;
                keys.right = right;
                this.keys = keys;
            }
        }, 50);
    }

    private loadKeys() {
        this.keys = KEY_LIST[this.type.toUpperCase()];
        if (!this.keys) {
            this.keys = KEY_LIST.QWERTY;
        }
        for (let i = 0; i < this.keys.normal.length; i++) {
            if (typeof this.keys.normal[i] === 'string') {
                this.keys.normal[i] = this.keys.normal[i].split('');
            }
            if (typeof this.keys.shift[i] === 'string') {
                this.keys.shift[i] = this.keys.shift[i].split('');
            }
        }
    }
}
